import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { cn } from '@/lib/utils';
import type { NavNode } from './navTree';

/**
 * Angela, 22 Oct 2026 (as Leave): a menu entry with `tabs` (HR › Manage Company
 * › Payroll, Staff Claim, Staff Promotion, Performance Review) shows its pages
 * as one row of tab buttons at the top of each of them — only the ones this
 * sign-in may open (the tree is already pruned).
 */
export function NavTabs({ group, isActive }: { group: NavNode; isActive: (node: NavNode) => boolean }) {
  const { t } = useTranslation('nav');
  const prefix = useLocalePrefix();
  const tabs = group.tabs ?? [];
  if (tabs.length < 2) return null;
  return (
    <div
      role="tablist"
      data-testid={`nav-tabs-${group.id}`}
      className="sticky top-[var(--page-heading-h,0px)] z-20 mb-4 flex gap-1 overflow-x-auto border-b border-border bg-background"
    >
      {tabs.map((tab) => {
        const active = isActive(tab);
        return (
          <Link
            key={tab.id}
            to={`${prefix}${tab.href ?? ''}`}
            role="tab"
            aria-selected={active}
            data-testid={`nav-tab-${tab.id}`}
            className={cn(
              'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              active ? 'border-[#FF8189] text-[#FF8189]' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t(tab.labelKey)}
          </Link>
        );
      })}
    </div>
  );
}
